
/**
 * Global error logging utility for CHRONA
 * Logs errors to console and to the Supabase logging_events table
 */
import { logger } from '@/services/logger';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Flag to enable/disable detailed logging
const ENABLE_DETAILED_LOGGING = process.env.NODE_ENV === 'development';

/**
 * Log an error with context information
 * @param error The error object or message
 * @param context Additional context information
 * @param notify Whether to show a toast notification
 */
export const logError = (error: unknown, context: Record<string, any> = {}, notify: boolean = true) => {
  // Extract error message
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  // Log to console
  console.error('CHRONA Error:', error);
  
  if (ENABLE_DETAILED_LOGGING) {
    console.error('Error Context:', context);
  }
  
  // Show toast notification if requested
  if (notify) {
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive"
    });
  }
  
  // Log to Supabase
  const persistError = async () => {
    try {
      // Use type assertion to bypass TypeScript checking
      const { error: logError } = await (supabase as any).from('logging_events').insert([{
        event_type: context.type || 'error',
        status: 'error',
        message: errorMessage,
        user_id: context.userId || null,
        meta: {
          ...context,
          stack: error instanceof Error ? error.stack : undefined,
          page: context.page || window.location.pathname,
          userAgent: navigator.userAgent,
        }
      }]);
      
      if (logError) {
        console.error('Failed to log error to database:', logError);
      }
    } catch (loggingError) {
      console.error('Failed to log error to backend:', loggingError);
    }
  };
  
  // Execute the logging
  persistError().catch(console.error);
};

/**
 * Initialize global error handling
 */
export const setupGlobalErrorHandling = () => {
  // Set up global error handler for uncaught exceptions
  window.addEventListener('error', (event) => {
    logError(event.error, {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      type: 'uncaught_exception'
    });
    
    // Don't prevent the default behavior
    return false;
  });
  
  // Set up global promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason, {
      type: 'unhandled_rejection',
      promise: event.promise
    });
    
    // Don't prevent the default behavior
    return false;
  });
  
  console.info('Global error handling initialized');
};

/**
 * Log API related errors
 */
export const logApiError = (endpoint: string, error: any, userId?: string) => {
  logError(error, {
    type: 'api_error',
    endpoint,
    userId,
  });
};

/**
 * Log authentication related errors
 */
export const logAuthError = (action: 'login' | 'signup' | 'logout' | 'reset' | string, error: any, email?: string) => {
  logError(error, {
    type: 'auth_error',
    action,
    email
  });
};

/**
 * Log Stripe related errors
 */
export const logStripeError = (error: any, eventType?: string, customerId?: string) => {
  logError(error, {
    type: 'stripe_error',
    eventType,
    customerId
  });
};

/**
 * Log email related errors
 */
export const logEmailError = (error: any, template?: string, recipient?: string) => {
  logError(error, {
    type: 'email_error',
    template,
    recipient
  });
};
